/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import cloneDeep from 'lodash.clonedeep';
import isString from 'lodash.isstring';
import {
  getUserRole,
  updateUserRoles
} from '@ion/core/src/entities/Roles';
import {IUserPreferences} from '@ion/core/src/preferences/types';
import {
  getExtendedAttributeValue,
  parseExtendedAttributes,
  setRel
} from '@ion/core/src/services/ConnectUtils';
import {
  Account,
  EntityStatus,
  GenericObject,
  Link,
  Role,
  User,
  UserPreferencesSettings
} from '@ion/core/src/types/entities';
import {
  Language,
  Preference,
  Preferences
} from '@ion/core/src/types/preferences';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {AugmentedFormState} from '../hooks/useFormState';
import {convertStatus, translateStatus} from '../utils/ComponentUtils';
import {updateUserPreferences} from './UserPreferencesEditor';

// available field names for the add/edit user form
export enum UserEditorFields {
  Username = 'username',
  EmailAddress = 'emailAddress',
  Group = 'group',
  Role = 'role',
  FirstName = 'firstName',
  LastName = 'lastName',
  Status = 'status',
  EndDate = 'endDate',
  Password = 'password',
  ConfirmPassword = 'confirmPassword',
  unitOfMeasure = 'unitOfMeasure',
  timeZone = 'timeZone',
  Applications ='applications',
  Addresses='addresses',
  PhoneNumbers='phoneNumbers',
  Language = 'language',
  ExtendedAttribute = 'extendedAttribute',
  isSsoUser = 'isSsoUser'
}

// defines the type definitions for createLangPreference params ...
export type CreateLangPreferenceType = {
  key: string;
  path: string;
  lng: string;
  namespace: string;
  isEditMode?: boolean;
  extAttrValue?: GenericObject;
};

export interface UserEditorFormState extends AugmentedFormState {
  user?: User;
}

export type SerializedArgs = {
  state: UserEditorFormState;
  preferences?: Preferences;
  isCurrentUser?: boolean;
  roles?: Role[];
  api?: IUserPreferences;
  namespace?: string;
  userAccount?: Account;
  enableAutoSave?: boolean;
};

// defines the role event target type definition ...
export interface TargetType {
  name: UserEditorFields,
  value: unknown;
  required?: boolean;
  error?: boolean;
};

const required    = {required: true};
const notRequired = {required: false};

/**
 * Provides the initial state of the user editor form, this state includes all named
 * editor fields and their requirements, as well as any initial values required, such
 * as the application for which the user is to be created, or a reference to a user,
 * in the case of edits.
 */
export const getInitialState = (user: User, roles?: Role[], namespace?: string) => {
  return deserialize(user, roles, namespace) || {
    [UserEditorFields.Username]        : required,
    [UserEditorFields.EmailAddress]    : required,
    [UserEditorFields.Group]           : required,
    [UserEditorFields.Role]            : required,
    [UserEditorFields.FirstName]       : required,
    [UserEditorFields.LastName]        : required,
    [UserEditorFields.Status]          : {...required, value: translateStatus(EntityStatus.Enabled)},
    [UserEditorFields.ConfirmPassword] : notRequired,
    [UserEditorFields.unitOfMeasure]   : notRequired,
    [UserEditorFields.timeZone]        : notRequired,
    [UserEditorFields.Language]        : notRequired
  };
};

/**
 * Provides the initial state of the user editor form for Ctc Admin, this state includes all named
 * editor fields and their requirements, as well as any initial values required, such
 * as the application for which the user is to be created, or a reference to a user,
 * in the case of edits.
 */
export const getInitialCtcAdminState = (user: User, roles?: Role[], namespace?: string) => {
  return deserializeCtcAdmin(user, roles, namespace) || {
    [UserEditorFields.Username]        : required,
    [UserEditorFields.EmailAddress]    : required,
    [UserEditorFields.Applications]    : required,
    [UserEditorFields.FirstName]       : required,
    [UserEditorFields.LastName]        : required,
    [UserEditorFields.Addresses]       : notRequired,
    [UserEditorFields.Status]          : {...required, value: EntityStatus.Enabled},
    [UserEditorFields.Group]           : notRequired,
    [UserEditorFields.ConfirmPassword] : notRequired,
    [UserEditorFields.PhoneNumbers]    : notRequired,
    [UserEditorFields.unitOfMeasure]   : notRequired,
    [UserEditorFields.timeZone]        : notRequired,
    [UserEditorFields.Language]        : notRequired
  };
};

/**
 * @private
 */
const createLangPreference = function({
  key,
  path,
  lng,
  namespace,
  isEditMode = false,
  extAttrValue = null
}: CreateLangPreferenceType): GenericObject {
  let value = {};
  const omitNameSpace = Object.keys(Language)?.findIndex((lang) => lang === lng) !== -1;
  if (omitNameSpace) {
    value = {
      [key] : {
        [path] : lng
      }
    };
  } else {
    value = {
      [key] : {
        [namespace] : {
          [path] : lng
        }
      }
    };
    if (isEditMode && extAttrValue?.[key]?.[path]) {
      value[key][path] = extAttrValue?.[key]?.[path];
    }
  }
  return value;
};

/**
 * Sets the language currently selected by the user in preferences
 * extended attribute.
 *
 * If the language is supported in all iOn applications then it is set at
 * the root level in preferences extended attribute else if the language
 * is supported only in a specific iOn application then it is set specific
 * to that application within the preferences extended attribute using
 * the appropriate namespace.
 *
 * If the lng parameter isn't passed, it is assumed that user has set the
 * preference as browser default and the preference key 'defaultLanguage'
 * is deleted from the preferences extended attribute.
 *
 * @param namespace current iOn application key
 * @param user the user being edited
 * @param lng current selected language
 */
export const setLanguageExtendedAttribute = (
  namespace: string,
  user: User,
  val: string
): GenericObject => {
  const lng  = val !== Language.default ? val : '';
  const key  = 'preferences';
  const path = Preference.defaultLanguage;

  if (!user) { // add mode
    if (lng) {
      const val = createLangPreference({key, path, lng, namespace});
      return {value: JSON.stringify(val)};
    }
    return null;
  } else { // edit mode
    const extAttrVal = parseExtendedAttributes(user, true);
    const extAttrValue = cloneDeep(extAttrVal);
    let val = {};
    val = createLangPreference({
      key,
      path,
      lng,
      namespace,
      extAttrValue,
      isEditMode : true
    });
    delete extAttrValue?.[key]?.[namespace]?.[path];
    delete extAttrValue?.[key]?.[path];
    return {
      ...user.extendedAttribute,
      value : JSON.stringify({
        [key] : {
          ...val?.preferences,
          ...extAttrValue?.preferences
        }
      })
    };
  }
};

/**
 * Gets the default language preferred by the user.
 *
 * If the 'defaultLanguage' is specific to that iOn application,
 * it is returned from that level at the preferences extended
 * attribute using the appropriate namespace else if it is a
 * common language supported by all iOn appications, then the
 * 'defaultLanguage' is returned from the root level of preferences
 * extended attribute.
 */
export const getLanguageExtendedAttribute = (namespace: string, user: User): string => {
  let defaultLng;
  const key = 'preferences';
  const path = Preference.defaultLanguage;
  const preferences = getExtendedAttributeValue(user, key);
  const preferedLng = preferences?.[namespace]?.[path];
  const lng = preferences?.[path];
  if (preferedLng) {
    defaultLng = preferedLng;
  } else if (lng) {
    defaultLng = Object.keys(Language)?.find((lang) => lang === lng) || undefined;
  }
  return defaultLng;
};

/**
 * Provides a utility which resolves the roles which are to be assigned to a new user,
 * existing user, or the current user.
 */
export const getRoles = (updated: GenericObject, role: any, roles: Role[]) => {
  if (role && !isString(role)) {
    const selected = role._self || role;
    if (updated.roles?.length) {
      return updateUserRoles(updated, selected as Link, roles);
    }
    return [setRel(selected, 'role')];
  }
  return updated?.roles;
};

/**
 * @serialize
 *
 * This function takes the form data from the user form and serializes it into a CTC User
 * object that gets passed to the addUser service call.
 *
 * @param {object} state: The form data that needs to be transformed into a user that can be
 *        passed to ctc to create the user.
 */
export const serialize = ({
  state,
  preferences,
  api,
  roles,
  isCurrentUser,
  enableAutoSave,
  namespace,
  userAccount
}: SerializedArgs) => {
  const {user, ...rest} = state;
  const serialized: GenericObject = {...user};
  const {href, rel} = state?.users || {};
  for (const [key, {value}] of Object.entries(rest)) {
    switch (key) {
      case UserEditorFields.Username:
        serialized.username = value.trim();
        break;
      case UserEditorFields.Group:
        serialized.account = !value?.href ? (user?.account || {href, rel}) : setRel(value, 'account');
        break;
      case UserEditorFields.Applications: {
        const rolesParam = value?.map((item) => {
          const {href, title, rel, status, error} = item?.roles;
          return {href, title, rel, status, error};
        });
        serialized.applications = value?.map((item) => item?.applications);
        serialized.roles = rolesParam;
        break;
      };
      case UserEditorFields.ConfirmPassword:
        serialized.password = value;
        break;
      case UserEditorFields.EndDate:
        serialized.terminationDate = value;
        break;
      case UserEditorFields.EmailAddress:
        serialized.email = value;
        break;
      case UserEditorFields.Role:
        serialized.roles = !isCurrentUser ? getRoles(serialized, value, roles) : user.roles;
        break;
      case UserEditorFields.Status :
        serialized.status = convertStatus(value);
        break;
      case UserEditorFields.Language: {
        const extAttrValue = setLanguageExtendedAttribute(namespace, user, value);
        serialized[UserEditorFields.ExtendedAttribute] = extAttrValue;
        break;
      };
      case UserEditorFields.isSsoUser:
        serialized.isSsoUser = rest[key];
        break;
      default:
        if (value || value === false) {
          serialized[key] = value;
        }
        break;
    }
  }
  delete serialized.confirmPassword;
  delete serialized.role;
  delete serialized.group;
  // [DLS TODO]: Remove auto-password updating to true once user change password prompt is in place.
  serialized.passwordUpdated = true;

  if (preferences) {
    preferences.accountTimeZone = userAccount?.timeZone;
    return updateUserPreferences(serialized, preferences, api);
  }
  if (enableAutoSave) {
    const value = {
      persistGridLayouts : true
    } as UserPreferencesSettings;
    if (serialized.extendedAttribute?.value) {
      const preferedLanguage = getLanguageExtendedAttribute(namespace, serialized);
      value.defaultLanguage  = preferedLanguage;
    }
    serialized.extendedAttribute = {
      ...serialized.extendedAttribute,
      value : safeJSONStringify({preferences: {[namespace]: value}})
    };
  }
  return serialized;
};

export interface EmptyLink extends Link {
  error?: string;
  status?: EntityStatus;
};

export const isValidAddress = ({
  phoneNumberValue,
  phoneNumberError,
  addressesValue,
  addressesError
}): boolean => {
  if (!phoneNumberValue && !phoneNumberError) {
    return addressesValue && addressesError;
  }
  return false;
};

export const createEmptyLink = (): EmptyLink => {
  return {href: '', title: '', rel: '', status: EntityStatus.Enabled, error: ''};
};

export const getDefaultUserRoles = () => {
  return {...createEmptyLink(), list: []};
};

export const getEmptyUserApplication = () => {
  return [{
    id           : Math.floor(Math.random() * 1000),
    applications : {...createEmptyLink()},
    roles        : {...createEmptyLink()}
  }];
};

/**
 * @deserialize
 *
 * This function takes a CTC user and serializes it into an user form consumable object that
 * can be pre-populated into the form. Essentially, it's the reverse of the serialize function
 * above.
 *
 * @param {GenericObject} user: CTC user that is to be deserialized into an user form object.
 */
export const deserialize = (user: User, roles: Role[], namespace: string): GenericObject => {
  if (user) {
    return {
      [UserEditorFields.Username] : {
        name  : UserEditorFields.Username,
        value : user.username
      },
      [UserEditorFields.EmailAddress] : {
        name  : UserEditorFields.EmailAddress,
        value : user.email
      },
      [UserEditorFields.Group] : {
        name  : UserEditorFields.Group,
        value : user.account
      },
      [UserEditorFields.Role] : {
        name  : UserEditorFields.Role,
        value : roles?.length ? getUserRole(user, roles) : ''
      },
      [UserEditorFields.FirstName] : {
        name  : UserEditorFields.FirstName,
        value : user.firstName
      },
      [UserEditorFields.LastName] : {
        name  : UserEditorFields.LastName,
        value : user.lastName
      },
      [UserEditorFields.Status] : {
        name  : UserEditorFields.Status,
        value : user?.status || null
      },
      [UserEditorFields.EndDate] : {
        name  : UserEditorFields.EndDate,
        value : user.terminationDate ? user.terminationDate : null
      },
      [UserEditorFields.Language] : {
        name  : UserEditorFields.Language,
        value : getLanguageExtendedAttribute(namespace, user)
      },
      user
    };
  }
};

/**
 *
 * This function takes a CTC user and serializes it into an user form consumable object that
 * can be pre-populated into the form. Essentially, it's the reverse of the serialize function
 * above.
 *
 * @param {GenericObject} user: CTC user that is to be deserialized into an user form object.
 */
export const deserializeCtcAdmin = (user: User, roles: Role[], namespace: string): GenericObject => {
  if (user) {
    return {
      [UserEditorFields.Username] : {
        name  : UserEditorFields.Username,
        value : user.username
      },
      [UserEditorFields.EmailAddress] : {
        name  : UserEditorFields.EmailAddress,
        value : user.email
      },
      [UserEditorFields.FirstName] : {
        name  : UserEditorFields.FirstName,
        value : user.firstName
      },
      [UserEditorFields.LastName] : {
        name  : UserEditorFields.LastName,
        value : user.lastName
      },
      [UserEditorFields.Status] : {
        name  : UserEditorFields.Status,
        value : user?.status || null
      },
      [UserEditorFields.EndDate] : {
        name  : UserEditorFields.EndDate,
        value : user.terminationDate ? user.terminationDate : null
      },
      [UserEditorFields.Language] : {
        name  : UserEditorFields.Language,
        value : getLanguageExtendedAttribute(namespace, user)
      },
      [UserEditorFields.Applications] : {
        name  : UserEditorFields.Applications,
        value : {applications: user.applications, roles: user.roles}
      },
      [UserEditorFields.Addresses] : {
        name  : UserEditorFields.Addresses,
        value : user.addresses
      },
      [UserEditorFields.PhoneNumbers] : {
        name  : UserEditorFields.PhoneNumbers,
        value : user.phoneNumbers
      },
      user
    };
  }
};
